import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { SwapVert, Star, DirectionsCar, Add, Delete, Edit, Warning } from "@mui/icons-material";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";
import {
  MutationPlan,
  PlacementMutation,
  FailedPlacement,
  DrivingTimeMutation,
  ShiftMutation,
} from "../../../stores/MutationDraft/types";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { SmallChip, TherapyChip, VisitTypeBadge } from "../../../atoms/Chip";
import { UserDto } from "../../../api-client";
import { useUsersByLocation } from "../../../stores/Calendar/calendar.queries";
import { useLocationId } from "../../../stores/User/User.hooks";
import { useTimeSlotDisplayData } from "../../../stores/Appointment/appointment.queries";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { getContrastTextColor } from "../../../themes/colorUtils";
import { getColorForRxNumber } from "../../../helpers/statusColor";
import { usePatientFromDraft } from "./usePatientFromDraft";
import { methodColorKey } from "../../../helpers/slotTherapy";

/** Compare two date strings by their calendar date (year, month, day). */
const isSameDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

// ─── Inline card body: shifts + placement ────────────────────────────────────
// Reusable for both the normal and rejected states of UnscheduledItem.

// ─── ShiftRow: per-shift component reading display data from RQ cache ────────

interface ShiftRowProps {
  shift: ShiftMutation;
  findUser: (id: string) => UserDto | undefined;
  showPatientName?: boolean;
}

const ShiftRow: React.FC<ShiftRowProps> = ({ shift: sh, findUser, showPatientName }) => {
  const { data: displayData } = useTimeSlotDisplayData(sh.timeSlotId, !!sh.timeSlotId);
  const { patient: resolvedPatient } = usePatientFromDraft(sh.timeSlotId, sh.patientId);
  const patientName = resolvedPatient
    ? `${resolvedPatient.firstName ?? ""} ${resolvedPatient.lastName ?? ""}`.trim() || undefined
    : undefined;
  const therapyAbbr = displayData?.therapy?.abbreviation;
  const therapyColorId = displayData?.therapy?.therapyMethodId
    ? methodColorKey(displayData.therapy.therapyMethodId)
    : undefined;
  const rxNumber = displayData?.rx?.rxNumber;
  const treatmentIndex = displayData?.treatmentIndex;
  const sublocality = displayData?.sublocality;
  const rxNumberColor = rxNumber ? getColorForRxNumber(rxNumber) : "transparent";
  const treatmentIndexColor = mobiliTheme.palette.info.main;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        alignItems: "center",
        p: 0.5,
        mb: 0.25,
        bgcolor: "warning.50",
        borderRadius: 0.5,
      }}
    >
      <Tooltip title="Verschoben">
        <SwapVert sx={{ fontSize: 16, color: "warning.dark", flexShrink: 0 }} />
      </Tooltip>
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ fontSize: "0.7rem", fontWeight: 500, whiteSpace: "nowrap" }}
      >
        {formatTime(sh.originalStart)}-{formatTime(sh.originalEnd)}→{formatTime(sh.newStart)}–
        {formatTime(sh.newEnd)}
      </Typography>
      {showPatientName && patientName && (
        <Typography
          variant="caption"
          sx={{ fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {patientName}
        </Typography>
      )}
      {rxNumber && (
        <SmallChip
          label={rxNumber}
          sx={{
            fontSize: "0.7rem",
            height: 20,
            width: "auto",
            backgroundColor: rxNumberColor,
            color: getContrastTextColor(rxNumberColor),
          }}
        />
      )}
      {treatmentIndex && (
        <SmallChip
          label={treatmentIndex}
          sx={{
            fontSize: "0.7rem",
            height: 20,
            width: "auto",
            backgroundColor: treatmentIndexColor,
            color: getContrastTextColor(treatmentIndexColor),
          }}
        />
      )}
      {therapyAbbr && (
        <TherapyChip
          therapyName={therapyAbbr}
          colorKey={therapyColorId}
          sx={{ fontSize: "0.7rem", height: 20, width: "auto" }}
        />
      )}
      {sublocality?.name && (
        <VisitTypeBadge
          visitType={displayData?.address?.addressType || ""}
          visitFee={displayData?.visitFee?.abbreviation}
          sublocality={sublocality}
          sx={{ fontSize: 11 }}
        />
      )}
      {sh.originalTherapistId !== sh.newTherapistId ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: "auto", flexShrink: 0 }}>
          <MobiliAvatar
            user={findUser(sh.originalTherapistId)}
            isTherapist
            sx={{ width: 18, height: 18, fontSize: "0.5rem" }}
          />
          <Typography variant="caption" sx={{ fontSize: "0.5rem" }}>
            ⟶
          </Typography>
          <MobiliAvatar
            user={findUser(sh.newTherapistId)}
            isTherapist
            sx={{ width: 18, height: 18, fontSize: "0.5rem" }}
          />
        </Box>
      ) : (
        <MobiliAvatar
          user={findUser(sh.newTherapistId)}
          isTherapist
          sx={{ width: 18, height: 18, fontSize: "0.5rem", ml: "auto", flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

// ─── PlacementRow: per-placement sub-component reading display data ──────────

interface PlacementRowProps {
  placement: PlacementMutation;
  findUser: (id: string) => UserDto | undefined;
  dayStr?: string;
  showPatientName?: boolean;
}

const PlacementRow: React.FC<PlacementRowProps> = ({
  placement,
  findUser,
  dayStr,
  showPatientName,
}) => {
  const { data: displayData } = useTimeSlotDisplayData(
    placement.timeSlotId ?? "",
    !!placement.timeSlotId,
  );
  const { patient: resolvedPatient } = usePatientFromDraft(
    placement.timeSlotId,
    placement.patientId,
  );
  const patientName = resolvedPatient
    ? `${resolvedPatient.firstName ?? ""} ${resolvedPatient.lastName ?? ""}`.trim() || undefined
    : undefined;
  const therapyAbbr = displayData?.therapy?.abbreviation;
  const therapyColorId = displayData?.therapy?.therapyMethodId
    ? methodColorKey(displayData.therapy.therapyMethodId)
    : undefined;
  const rxNumber = displayData?.rx?.rxNumber;
  const treatmentIndex = displayData?.treatmentIndex;
  const sublocality = displayData?.sublocality;
  const rxNumberColor = rxNumber ? getColorForRxNumber(rxNumber) : "transparent";
  const treatmentIndexColor = mobiliTheme.palette.info.main;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        alignItems: "center",
        p: 0.5,
        mb: 0.25,
        bgcolor: "success.50",
        borderRadius: 0.5,
      }}
    >
      <Tooltip title="Neuer Termin">
        <Star sx={{ fontSize: 16, color: "#2e7d32", flexShrink: 0 }} />
      </Tooltip>
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ fontSize: "0.7rem", fontWeight: 500, whiteSpace: "nowrap" }}
      >
        {dayStr && placement.start && isSameDay(placement.start, dayStr)
          ? `${formatTime(placement.start)}–${formatTime(placement.end)}`
          : `${formatDateUI(placement.start)} ${formatTime(placement.start)}–${formatTime(placement.end)}`}
      </Typography>
      {showPatientName && patientName && (
        <Typography
          variant="caption"
          sx={{ fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {patientName}
        </Typography>
      )}
      {rxNumber && (
        <SmallChip
          label={rxNumber}
          sx={{
            fontSize: "0.7rem",
            height: 20,
            width: "auto",
            backgroundColor: rxNumberColor,
            color: getContrastTextColor(rxNumberColor),
          }}
        />
      )}
      {treatmentIndex && (
        <SmallChip
          label={treatmentIndex}
          sx={{
            fontSize: "0.7rem",
            height: 20,
            width: "auto",
            backgroundColor: treatmentIndexColor,
            color: getContrastTextColor(treatmentIndexColor),
          }}
        />
      )}
      {therapyAbbr && (
        <TherapyChip
          therapyName={therapyAbbr}
          colorKey={therapyColorId}
          sx={{ fontSize: "0.7rem", height: 20, width: "auto" }}
        />
      )}
      {sublocality?.name && (
        <VisitTypeBadge
          visitType={displayData?.address?.addressType || ""}
          visitFee={displayData?.visitFee?.abbreviation}
          sublocality={sublocality}
          sx={{ fontSize: 11 }}
        />
      )}
      <MobiliAvatar
        user={findUser(placement.therapistId ?? "")}
        isTherapist
        sx={{ width: 20, height: 20, fontSize: "0.55rem", ml: "auto", flexShrink: 0 }}
      />
    </Box>
  );
};

// ─── SkippedRow: per-skipped sub-component reading display data ──────────────

interface SkippedRowProps {
  skipped: FailedPlacement;
}

const SkippedRow: React.FC<SkippedRowProps> = ({ skipped }) => {
  const { data: displayData } = useTimeSlotDisplayData(skipped.timeSlotId, !!skipped.timeSlotId);
  const { patient: resolvedPatient } = usePatientFromDraft(skipped.timeSlotId, skipped.patientId);
  const patientName = resolvedPatient
    ? `${resolvedPatient.firstName ?? ""} ${resolvedPatient.lastName ?? ""}`.trim() || undefined
    : undefined;
  const therapyAbbr = displayData?.therapy?.abbreviation;
  const therapyColorId = displayData?.therapy?.therapyMethodId
    ? methodColorKey(displayData.therapy.therapyMethodId)
    : undefined;
  const rxNumber = displayData?.rx?.rxNumber;
  const treatmentIndex = displayData?.treatmentIndex;
  const sublocality = displayData?.sublocality;
  const rxNumberColor = rxNumber ? getColorForRxNumber(rxNumber) : "transparent";
  const treatmentIndexColor = mobiliTheme.palette.info.main;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        alignItems: "center",
        p: 0.5,
        mb: 0.25,
        bgcolor: "error.50",
        borderRadius: 0.5,
      }}
    >
      <Tooltip title={`Nicht platziert: ${skipped.reason}`}>
        <Warning sx={{ fontSize: 16, color: "error.dark", flexShrink: 0 }} />
      </Tooltip>
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ fontSize: "0.7rem", fontWeight: 500, whiteSpace: "nowrap" }}
      >
        {patientName ?? skipped.appointmentId?.slice(0, 8)}
      </Typography>
      {rxNumber && (
        <SmallChip
          label={rxNumber}
          sx={{
            fontSize: "0.7rem",
            height: 20,
            width: "auto",
            backgroundColor: rxNumberColor,
            color: getContrastTextColor(rxNumberColor),
          }}
        />
      )}
      {treatmentIndex && (
        <SmallChip
          label={treatmentIndex}
          sx={{
            fontSize: "0.7rem",
            height: 20,
            width: "auto",
            backgroundColor: treatmentIndexColor,
            color: getContrastTextColor(treatmentIndexColor),
          }}
        />
      )}
      {therapyAbbr && (
        <TherapyChip
          therapyName={therapyAbbr}
          colorKey={therapyColorId}
          sx={{ fontSize: "0.7rem", height: 20, width: "auto" }}
        />
      )}
      {sublocality?.name && (
        <VisitTypeBadge
          visitType={displayData?.address?.addressType || ""}
          visitFee={displayData?.visitFee?.abbreviation}
          sublocality={sublocality}
          sx={{ fontSize: 11 }}
        />
      )}
    </Box>
  );
};

// ─── MutationPlanBody ────────────────────────────────────────────────────────

interface MutationPlanBodyProps {
  draft: MutationPlan;
  dayStr?: string;
  showPatientName?: boolean;
}

const MutationPlanBody: React.FC<MutationPlanBodyProps> = ({ draft, dayStr, showPatientName }) => {
  const plan = draft;
  const drivingTimes: DrivingTimeMutation[] = draft.drivingTimes ?? [];

  const locationId = useLocationId();
  const { data: allUsers = [] } = useUsersByLocation(locationId, undefined, false, !!locationId);
  const findUser = (therapistId: string): UserDto | undefined =>
    allUsers.find((u: UserDto) => u.id === therapistId);

  // const timeSlotId = plan.placements[0]?.timeSlotId;
  // const { data: displayData } = useTimeSlotDisplayData(timeSlotId ?? "", !!timeSlotId);
  // const rxNumber = displayData?.rx?.rxNumber;
  // const treatmentIndex = displayData?.treatmentIndex;
  // const sublocality = displayData?.sublocality;
  // const rxNumberColor = rxNumber ? getColorForRxNumber(rxNumber) : "transparent";
  // const treatmentIndexColor = mobiliTheme.palette.info.main;

  return (
    <>
      {/* Placements section */}
      {plan.placements.length > 0 &&
        plan.placements.map((placement, idx) => (
          <PlacementRow
            key={`${placement.timeSlotId ?? placement.start}-${idx}`}
            placement={placement}
            findUser={findUser}
            dayStr={dayStr}
            showPatientName={showPatientName}
          />
        ))}

      {/* Skipped (unplaced) section */}
      {plan.skipped &&
        plan.skipped.length > 0 &&
        plan.skipped.map((sk, idx) => (
          <SkippedRow key={`${sk.appointmentId}-${idx}`} skipped={sk} />
        ))}

      {/* Shifts section */}
      {plan.shifts.length > 0 &&
        plan.shifts.map((sh) => (
          <ShiftRow
            key={sh.timeSlotId}
            shift={sh}
            findUser={findUser}
            showPatientName={showPatientName}
          />
        ))}

      {/* Driving time changes section */}
      {drivingTimes.length > 0 && (
        <>
          {/* <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              fontWeight: 600,
              color: "info.dark",
              display: "block",
              mt: 0.5,
              mb: 0.25,
            }}
          >
            FAHRTZEITEN:
          </Typography> */}
          {drivingTimes.map((dt, idx) => {
            const ActionIcon =
              dt.action === "create" ? Add : dt.action === "delete" ? Delete : Edit;
            const actionLabel =
              dt.action === "create" ? "Neu" : dt.action === "delete" ? "Entfernen" : "Ändern";
            const actionColor =
              dt.action === "create"
                ? "success.main"
                : dt.action === "delete"
                  ? "error.main"
                  : "warning.main";
            const bgColor =
              dt.action === "create"
                ? "success.50"
                : dt.action === "delete"
                  ? "error.50"
                  : "info.50";

            return (
              <Box
                key={dt.id ?? `driving-${idx}`}
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  p: 0.5,
                  mb: 0.25,
                  bgcolor: bgColor,
                  borderRadius: 0.5,
                }}
              >
                <DirectionsCar sx={{ fontSize: 16, color: "info.dark", flexShrink: 0 }} />
                <ActionIcon sx={{ fontSize: 14, color: actionColor, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: "0.7rem", whiteSpace: "nowrap" }}
                >
                  {actionLabel}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500, whiteSpace: "nowrap" }}
                >
                  {dt.start && dt.end
                    ? dayStr && isSameDay(dt.start, dayStr)
                      ? `${formatTime(dt.start)} – ${formatTime(dt.end)}`
                      : `${formatDateUI(dt.start)} ${formatTime(dt.start)} – ${formatTime(dt.end)}`
                    : dt.durationMinutes
                      ? `${dt.durationMinutes} min`
                      : ""}
                </Typography>
                <MobiliAvatar
                  user={findUser(dt.therapistId)}
                  isTherapist
                  sx={{ width: 18, height: 18, fontSize: "0.5rem", ml: "auto", flexShrink: 0 }}
                />
              </Box>
            );
          })}
        </>
      )}
    </>
  );
};

export default MutationPlanBody;
